import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/* OPERATIVA de recuperación de contraseña — recorrido VISUAL (demo).
   Muestra los 4 pasos tal cual los vive el cliente, sin necesitar email real:
   1) pedir email  2) aviso "te hemos enviado un correo" (+ vista del email)
   3) crear nueva contraseña (pantalla /reset-password)  4) listo.
   El estilo es el de marca (blanco/mint). El flujo REAL usa:
   /auth/forgot-password → email con enlace → /reset-password (token) → login. */

const TEAL = '#2C6675';
const TEAL2 = '#3C8696';

const S = {
  wrap: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
    background: 'radial-gradient(130% 70% at 50% 0%,#E1F4EF 0%,#EAF6F4 55%)',
    fontFamily: "'Montserrat', system-ui, sans-serif", padding: '34px 18px 48px',
  },
  logos: { display: 'flex', alignItems: 'center', gap: 18, marginBottom: 16 },
  steps: { display: 'flex', gap: 8, marginBottom: 18 },
  card: {
    background: '#fff', borderRadius: 22, boxShadow: '0 18px 50px rgba(44,107,118,.16)',
    width: '100%', maxWidth: 440, padding: '30px 28px 26px', boxSizing: 'border-box',
  },
  title: { margin: '0 0 4px', fontSize: 23, color: '#16323a', fontWeight: 800, textAlign: 'center' },
  desc: { margin: '0 0 20px', color: '#33474d', fontSize: 14, textAlign: 'center', lineHeight: 1.5 },
  label: { fontWeight: 700, fontSize: 13, color: '#16323a' },
  input: {
    width: '100%', height: 46, borderRadius: 12, border: '1px solid #dbe6e7', padding: '0 14px',
    fontSize: 15, outline: 'none', boxSizing: 'border-box', margin: '6px 0 16px',
    fontFamily: "'Montserrat', system-ui, sans-serif",
  },
  btn: (dis) => ({
    height: 48, borderRadius: 12, border: 'none', width: '100%', cursor: dis ? 'default' : 'pointer',
    fontWeight: 800, fontSize: 15.5, fontFamily: "'Montserrat', system-ui, sans-serif",
    background: dis ? '#cfe0e1' : `linear-gradient(135deg,${TEAL2},${TEAL})`, color: '#fff',
  }),
  ghost: {
    height: 44, borderRadius: 12, width: '100%', cursor: 'pointer', marginTop: 10,
    fontWeight: 600, fontSize: 14, fontFamily: "'Montserrat', system-ui, sans-serif",
    background: 'rgba(60,134,150,.10)', color: TEAL, border: '1px solid rgba(60,134,150,.35)',
  },
};

const Logos = () => (
  <div style={S.logos}>
    <img src="/logo-pd.png" alt="Pilar Domínguez" style={{ height: 34, width: 'auto' }} />
    <span style={{ width: 1, height: 28, background: 'rgba(44,107,118,.3)' }} />
    <img src="/logo-mefir-trans.png" alt="Instituto MEFIR" style={{ height: 30, width: 'auto' }} />
  </div>
);

const Icono = ({ children }) => (
  <div style={{ width: 62, height: 62, borderRadius: '50%', background: 'rgba(60,134,150,.12)', display: 'grid', placeItems: 'center', margin: '2px auto 16px' }}>{children}</div>
);

export default function RecuperacionPasswordDemo({ onSalir = () => {} }) {
  const { t } = useTranslation('auth');
  const [paso, setPaso] = useState(0);
  const [email, setEmail] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const pwOk = pw1.length >= 8 && pw1 === pw2;
  const nombreDemo = (email.split('@')[0] || t('demo.clienteFallback'));

  return (
    <div style={S.wrap}>
      <Logos />
      <div style={S.steps}>
        {[0, 1, 2, 3].map(i => (
          <span key={i} style={{ width: i === paso ? 28 : 9, height: 9, borderRadius: 999, background: i <= paso ? TEAL : 'rgba(44,107,118,.22)', transition: '.25s' }} />
        ))}
      </div>

      {/* PASO 1 · Pedir email */}
      {paso === 0 && (
        <div style={S.card}>
          <h1 style={S.title}>{t('recuperar.titulo')}</h1>
          <p style={S.desc}>{t('demo.pedirSub')}</p>
          <label style={S.label}>{t('comun.email')}</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value.trim())} placeholder={t('comun.emailPlaceholder')} style={S.input} />
          <button style={S.btn(!emailOk)} disabled={!emailOk} onClick={() => setPaso(1)}>{t('recuperar.enviarEnlace')}</button>
          <button style={S.ghost} onClick={onSalir}>{t('comun.volverLogin')}</button>
        </div>
      )}

      {/* PASO 2 · Aviso de correo enviado + vista del email */}
      {paso === 1 && (
        <div style={S.card}>
          <Icono>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m3 7 9 6 9-6" /></svg>
          </Icono>
          <h1 style={S.title}>{t('demo.revisaCorreo')}</h1>
          <p style={S.desc}>{t('demo.enviado1')}<b>{email || t('demo.tuEmail')}</b>{t('demo.enviado2')}</p>

          {/* Vista simulada del email que recibe el cliente */}
          <div style={{ border: '1px solid #e2eeed', borderRadius: 14, overflow: 'hidden', margin: '4px 0 14px' }}>
            <div style={{ background: '#f5faf9', padding: '10px 14px', fontSize: 12, color: '#5a7176', borderBottom: '1px solid #e2eeed' }}>
              <b style={{ color: '#16323a' }}>Método Pilar Domínguez</b> · {t('demo.emailAsunto')}
            </div>
            <div style={{ padding: '16px 16px 18px' }}>
              <p style={{ margin: '0 0 12px', fontSize: 13.5, color: '#2f4247' }}>{t('demo.emailHola', { nombre: nombreDemo })}</p>
              <button style={{ ...S.btn(false), height: 42, fontSize: 14 }} onClick={() => setPaso(2)}>{t('demo.crearNueva')}</button>
              <p style={{ margin: '12px 0 0', fontSize: 11.5, color: '#8a9ca0' }}>{t('demo.noHasSidoTu')}</p>
            </div>
          </div>
          <p style={{ fontSize: 12, color: '#8a9ca0', textAlign: 'center', margin: 0 }}>{t('demo.asiSeVe')}</p>
        </div>
      )}

      {/* PASO 3 · Crear nueva contraseña (pantalla /reset-password) */}
      {paso === 2 && (
        <div style={S.card}>
          <h1 style={S.title}>{t('reset.creaTitulo')}</h1>
          <p style={S.desc}>{t('reset.creaSub')}</p>
          <label style={S.label}>{t('reset.nuevaPassword')}</label>
          <input type="password" value={pw1} onChange={e => setPw1(e.target.value)} placeholder={t('comun.min8Placeholder')} style={S.input} />
          <label style={S.label}>{t('comun.repitePassword')}</label>
          <input type="password" value={pw2} onChange={e => setPw2(e.target.value)} placeholder={t('comun.repitePlaceholder')} style={S.input} />
          {pw2 && pw1 !== pw2 && <p style={{ color: '#d9534f', fontSize: 12.5, margin: '-10px 0 10px' }}>{t('comun.noCoinciden')}</p>}
          <button style={S.btn(!pwOk)} disabled={!pwOk} onClick={() => setPaso(3)}>{t('comun.guardarPassword')}</button>
        </div>
      )}

      {/* PASO 4 · Listo */}
      {paso === 3 && (
        <div style={{ ...S.card, textAlign: 'center' }}>
          <Icono>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
          </Icono>
          <h1 style={S.title}>{t('demo.actualizada')}</h1>
          <p style={S.desc}>{t('demo.yaPuedesEntrar')}</p>
          <button style={S.btn(false)} onClick={onSalir}>{t('comun.irLogin')}</button>
          <button style={S.ghost} onClick={() => setPaso(0)}>{t('demo.verOtraVez')}</button>
        </div>
      )}
    </div>
  );
}
